import React, { useEffect, useRef, useState, useCallback } from 'react';
import { init, dispose } from 'klinecharts';
import { stockSearchService } from '../../config/api';
import { useStockStream } from '../../hooks/useStockStream';

// Available interval options
const INTERVAL_OPTIONS = [
    { value: '1m', label: '1 Min' },
    { value: '3m', label: '3 Min' },
    { value: '5m', label: '5 Min' },
    { value: '10m', label: '10 Min' },
    { value: '15m', label: '15 Min' },
    { value: '30m', label: '30 Min' },
    { value: '1h', label: '1 Hour' },
    { value: '2h', label: '2 Hour' },
    { value: '3h', label: '3 Hour' },
    { value: '4h', label: '4 Hour' },
    { value: '1d', label: '1 Day' },
    { value: '1w', label: '1 Week' },
    { value: '1M', label: '1 Month' }
];

// Available technical indicator options - all KLineCharts built-in indicators
const INDICATOR_OPTIONS = [
    // Overlay Indicators (render on main chart)
    { value: 'MA', label: 'Moving Average', category: 'overlay', description: 'Simple moving average of closing prices' },
    { value: 'EMA', label: 'Exponential MA', category: 'overlay', description: 'Weighted moving average giving more weight to recent prices' },
    { value: 'SMA', label: 'Smoothed MA', category: 'overlay', description: 'Smoothed simple moving average' },
    { value: 'BOLL', label: 'Bollinger Bands', category: 'overlay', description: 'Volatility bands above and below MA' },
    { value: 'SAR', label: 'Parabolic SAR', category: 'overlay', description: 'Stop and reverse indicator for trend direction' },
    { value: 'BBI', label: 'Bull Bear Index', category: 'overlay', description: 'Average of multiple moving averages' },

    // Sub-pane Indicators (render in separate pane below chart)
    { value: 'VOL', label: 'Volume', category: 'sub-pane', description: 'Trading volume with MA' },
    { value: 'MACD', label: 'MACD', category: 'sub-pane', description: 'Moving average convergence/divergence' },
    { value: 'KDJ', label: 'KDJ', category: 'sub-pane', description: 'Stochastic oscillator variant' },
    { value: 'RSI', label: 'RSI', category: 'sub-pane', description: 'Relative strength index (0-100)' },
    { value: 'BIAS', label: 'BIAS', category: 'sub-pane', description: 'Deviation from moving average' },
    { value: 'BRAR', label: 'BRAR', category: 'sub-pane', description: 'Buying/selling pressure indicator' },
    { value: 'CCI', label: 'CCI', category: 'sub-pane', description: 'Commodity channel index' },
    { value: 'DMI', label: 'DMI', category: 'sub-pane', description: 'Directional movement index' },
    { value: 'CR', label: 'CR', category: 'sub-pane', description: 'Energy accumulation indicator' },
    { value: 'PSY', label: 'PSY', category: 'sub-pane', description: 'Psychological line indicator' },
    { value: 'DMA', label: 'DMA', category: 'sub-pane', description: 'Double moving average' },
    { value: 'TRIX', label: 'TRIX', category: 'sub-pane', description: 'Triple exponential average' },
    { value: 'OBV', label: 'OBV', category: 'sub-pane', description: 'On-balance volume' },
    { value: 'VR', label: 'VR', category: 'sub-pane', description: 'Volume ratio' },
    { value: 'WR', label: 'Williams %R', category: 'sub-pane', description: 'Williams percent range' },
    { value: 'MTM', label: 'Momentum', category: 'sub-pane', description: 'Rate of price change' },
    { value: 'EMV', label: 'EMV', category: 'sub-pane', description: 'Ease of movement' },
    { value: 'AO', label: 'Awesome Oscillator', category: 'sub-pane', description: 'Market momentum indicator' },
    { value: 'ROC', label: 'ROC', category: 'sub-pane', description: 'Rate of change' },
    { value: 'PVT', label: 'PVT', category: 'sub-pane', description: 'Price volume trend' },
    { value: 'AVP', label: 'Avg Price', category: 'sub-pane', description: 'Average price line' },
];

// Get maximum days to fetch based on interval type
// These limits are based on Upstox API constraints
const getMaxDaysForInterval = (interval) => {
    if (!interval) return 60;
    const normalized = interval.toLowerCase();

    // Minutes: max 30 days from Upstox, use 14 days for better performance
    if (normalized.endsWith('m') && !normalized.endsWith('mo')) {
        return 14;
    }
    // Hours: max 90 days from Upstox, use 30 days
    if (normalized.endsWith('h')) {
        return 30;
    }
    // Days: max 365 days from Upstox, use 60 days
    if (normalized.endsWith('d')) {
        return 60;
    }
    // Weeks: max 730 days from Upstox, use 180 days
    if (normalized.endsWith('w')) {
        return 180;
    }
    // Months: max 1825 days from Upstox, use 365 days
    if (normalized === '1m' || normalized.endsWith('mo')) {
        return 365;
    }
    return 60; // Default
};

const StockChart = ({
    symbol = 'TITAN',
    interval: initialInterval = '1h',
    theme = 'light',
    height = '600px',
    onTrendLinesChange = null, // Callback when trend lines change
    enableLiveStreamDefault = false, // Default state for live streaming
    exchange = 'NSE',
    instrumentType = 'EQ',
}) => {
    const chartContainerRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [drawings, setDrawings] = useState([]);
    const [indicators, setIndicators] = useState([]);
    const [overlayIds, setOverlayIds] = useState([]);
    const [trendLines, setTrendLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInterval, setSelectedInterval] = useState(initialInterval);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [dataRange, setDataRange] = useState({ from: null, to: null });
    const [noMoreData, setNoMoreData] = useState(false);
    const [liveStreamEnabled, setLiveStreamEnabled] = useState(enableLiveStreamDefault);
    const [activeIndicators, setActiveIndicators] = useState(new Set());
    // Determine if dark theme based on prop
    const isDarkTheme = theme === 'dark';

    // Refs to track data boundaries for lazy loading
    const oldestTimestampRef = useRef(null);
    const newestTimestampRef = useRef(null);
    const isLoadingMoreRef = useRef(false);
    const noMoreDataRef = useRef(false);

    // Store symbol and interval in refs to avoid stale closures
    const symbolRef = useRef(symbol);
    const intervalRef = useRef(selectedInterval);

    // Update refs when props change
    useEffect(() => {
        symbolRef.current = symbol;
        intervalRef.current = selectedInterval;
    }, [symbol, selectedInterval]);

    // Handle live tick data from stream
    const handleLiveTick = useCallback((tickData) => {
        if (!chartInstanceRef.current || !tickData) return;

        console.log('[Live Stream] Received tick:', tickData);

        // Transform tick data to chart format
        const candle = {
            timestamp: tickData.timestampLong || Date.now(),
            open: parseFloat(tickData.openPrice) || 0,
            high: parseFloat(tickData.highPrice) || 0,
            low: parseFloat(tickData.lowPrice) || 0,
            close: parseFloat(tickData.closePrice) || 0,
            volume: parseFloat(tickData.volume) || 0,
        };

        // Update the chart with the new candle
        // updateData will update the last candle if timestamp matches, or add new candle
        chartInstanceRef.current.updateData(candle);

        // Update newest timestamp
        if (candle.timestamp > (newestTimestampRef.current || 0)) {
            newestTimestampRef.current = candle.timestamp;
            setDataRange(prev => ({
                ...prev,
                to: new Date(candle.timestamp).toLocaleDateString()
            }));
        }
    }, []);

    // Use the stock stream hook
    const {
        isConnected: isStreamConnected,
        lastTick,
        error: streamError,
        tickCount,
    } = useStockStream(symbol, {
        enabled: liveStreamEnabled,
        exchange,
        instrumentType,
        interval: selectedInterval,
        emitMode: 'live', // Use live mode for streaming to the chart
        onData: handleLiveTick,
        onConnect: () => console.log('[Live Stream] Connected'),
        onDisconnect: () => console.log('[Live Stream] Disconnected'),
        onError: (err) => console.error('[Live Stream] Error:', err),
    });


    // Transform API data to KLineCharts format
    const transformCandleData = useCallback((candleData) => {
        if (!candleData || !Array.isArray(candleData)) return [];

        return candleData
            .filter(candle => candle && candle.timestampLong)
            .map(candle => ({
                timestamp: candle.timestampLong,
                open: parseFloat(candle.openPrice) || 0,
                high: parseFloat(candle.highPrice) || 0,
                low: parseFloat(candle.lowPrice) || 0,
                close: parseFloat(candle.closePrice) || 0,
                volume: parseFloat(candle.volume) || 0,
            }))
            .sort((a, b) => a.timestamp - b.timestamp);
    }, []);

    // Extract symbol name (remove exchange prefix if present)
    const getStockSymbol = (sym) => {
        const s = sym || symbolRef.current;
        return s.includes(':') ? s.split(':')[1] : s;
    };

    // Fetch historical data from API (initial load)
    const fetchHistoricalData = async (chartInstance, sym, intv) => {
        try {
            setLoading(true);
            setError(null);
            setNoMoreData(false);
            noMoreDataRef.current = false;

            // Calculate date range based on interval type
            // Upstox API has different limits: minutes=30d, hours=90d, days=365d
            const maxDays = getMaxDaysForInterval(intv);
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - maxDays);

            console.log(`[Initial Load] Using ${maxDays} days for interval '${intv}'`);

            const from = startDate.toISOString();
            const to = endDate.toISOString();
            const stockSymbol = getStockSymbol(sym);

            console.log(`[Initial Load] Fetching data for ${stockSymbol} from ${from} to ${to}`);

            const response = await stockSearchService.getHistory(stockSymbol, from, to, intv, 'NSE', 'EQ');
            const dataList = transformCandleData(response.data);

            if (dataList.length === 0) {
                console.warn('No candle data received from API');
                setError('No data available for this stock');
                setLoading(false);
                return;
            }

            // Update data boundaries
            oldestTimestampRef.current = dataList[0].timestamp;
            newestTimestampRef.current = dataList[dataList.length - 1].timestamp;
            setDataRange({
                from: new Date(dataList[0].timestamp).toLocaleDateString(),
                to: new Date(dataList[dataList.length - 1].timestamp).toLocaleDateString()
            });

            console.log(`[Initial Load] Loaded ${dataList.length} candles, range: ${new Date(oldestTimestampRef.current).toISOString()} to ${new Date(newestTimestampRef.current).toISOString()}`);
            chartInstance.applyNewData(dataList);
            setLoading(false);

        } catch (err) {
            console.error('Error fetching historical data:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load stock data');
            setLoading(false);
        }
    };

    // Fetch more historical data (lazy loading when scrolling left)
    const fetchMoreHistoricalData = async (chartInstance, timestamp) => {
        // Prevent concurrent requests - use ref to avoid stale closure
        if (isLoadingMoreRef.current || noMoreDataRef.current) {
            console.log('[Load More] Skipping - already loading or no more data');
            return;
        }

        // Check if we have valid oldest timestamp
        if (!oldestTimestampRef.current) {
            console.log('[Load More] Skipping - no oldest timestamp yet');
            return;
        }

        try {
            isLoadingMoreRef.current = true;
            setLoadingMore(true);

            const stockSymbol = getStockSymbol();
            const intv = intervalRef.current;

            // Calculate date range based on interval type
            const maxDays = getMaxDaysForInterval(intv);
            const endDate = new Date(oldestTimestampRef.current);
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - maxDays);

            console.log(`[Load More] Using ${maxDays} days for interval '${intv}'`);

            const from = startDate.toISOString();
            const to = endDate.toISOString();

            console.log(`[Load More] Fetching older data for ${stockSymbol} from ${from} to ${to}`);

            const response = await stockSearchService.getHistory(stockSymbol, from, to, intv, 'NSE', 'EQ');
            const dataList = transformCandleData(response.data);

            if (dataList.length === 0) {
                console.log('[Load More] No more historical data available');
                noMoreDataRef.current = true;
                setNoMoreData(true);
                setLoadingMore(false);
                isLoadingMoreRef.current = false;
                return;
            }

            // Filter out data we already have (avoid duplicates)
            const newData = dataList.filter(d => d.timestamp < oldestTimestampRef.current);

            if (newData.length === 0) {
                console.log('[Load More] No new data after filtering duplicates');
                noMoreDataRef.current = true;
                setNoMoreData(true);
                setLoadingMore(false);
                isLoadingMoreRef.current = false;
                return;
            }

            // Update oldest timestamp
            oldestTimestampRef.current = newData[0].timestamp;
            setDataRange(prev => ({
                ...prev,
                from: new Date(newData[0].timestamp).toLocaleDateString()
            }));

            console.log(`[Load More] Adding ${newData.length} older candles, new oldest: ${new Date(oldestTimestampRef.current).toISOString()}`);

            // Prepend the new data to the chart
            chartInstance.applyMoreData(newData);
            setLoadingMore(false);
            isLoadingMoreRef.current = false;

        } catch (err) {
            console.error('[Load More] Error fetching more data:', err);
            setLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    };

    useEffect(() => {
        console.log('StockChart useEffect running for symbol:', symbol, 'interval:', selectedInterval);

        // Reset state for new symbol/interval
        oldestTimestampRef.current = null;
        newestTimestampRef.current = null;
        isLoadingMoreRef.current = false;
        noMoreDataRef.current = false;
        setNoMoreData(false);
        setError(null);
        setDataRange({ from: null, to: null });

        let chartInstance = null;

        if (chartContainerRef.current) {
            try {
                console.log('Attempting to initialize chart...');
                // Initialize the chart with empty data first to avoid 'list' undefined error
                chartInstance = init(chartContainerRef.current, {
                    customApi: {
                        formatDate: (dateTimeFormat, timestamp) => {
                            const date = new Date(timestamp);
                            return date.toLocaleString();
                        }
                    }
                });
                chartInstanceRef.current = chartInstance;
                console.log('Chart instance:', chartInstance);

                if (!chartInstance) {
                    console.error('Chart initialization returned null');
                    return;
                }

                // Initialize with empty data to prevent errors
                chartInstance.applyNewData([]);

                // Set up the loadMore callback for lazy loading
                // This callback uses refs to get current values, avoiding stale closures
                chartInstance.loadMore((timestamp) => {
                    console.log('[LoadMore Callback] Triggered at timestamp:', timestamp, new Date(timestamp).toISOString());
                    if (chartInstanceRef.current) {
                        fetchMoreHistoricalData(chartInstanceRef.current, timestamp);
                    }
                });

                // Fetch real data from API with current symbol and interval
                fetchHistoricalData(chartInstance, symbol, selectedInterval);

            } catch (error) {
                console.error('Error in chart setup:', error);
                setError('Failed to initialize chart');
            }
        } else {
            console.error('chartContainerRef.current is null');
        }

        return () => {
            // Dispose chart on unmount
            if (chartContainerRef.current) {
                dispose(chartContainerRef.current);
                chartInstanceRef.current = null;
            }
        };
    }, [symbol, selectedInterval]); // Only depend on symbol and interval, not on callbacks

    // Dynamic theme applying using DaisyUI resolved colors
    const applyThemeStyles = useCallback(() => {
        if (!chartInstanceRef.current || !chartContainerRef.current) return;

        // Helper to resolve current CSS variable values via classes
        // We append to the chart container to ensure we inherit the correct scope/theme context
        const getStyle = (className, prop = 'color') => {
            const el = document.createElement('div');
            el.className = className;
            el.style.visibility = 'hidden';
            el.style.position = 'absolute';
            el.style.pointerEvents = 'none';
            // Append to the specific container to inherit local theme/context
            chartContainerRef.current.appendChild(el);
            const value = window.getComputedStyle(el)[prop];
            chartContainerRef.current.removeChild(el);
            return value;
        };

        const textColor = getStyle('text-base-content');
        const gridColor = getStyle('border-base-300', 'borderColor');
        const crosshairBg = getStyle('bg-base-300', 'backgroundColor');
        const fontFamily = getStyle('font-sans', 'fontFamily'); // Get app font (Poppins/Inter)

        // For crosshair text, we want good contrast against the crosshair background
        // DaisyUI doesn't always have a strict "content on base-300" class, but text-base-content is usually safe
        const crosshairText = getStyle('text-base-content');

        const upColor = getStyle('text-success');
        const downColor = getStyle('text-error');
        const bgColor = getStyle('bg-base-100', 'backgroundColor');

        const styles = {
            grid: {
                horizontal: { color: gridColor, style: 'dashed', dashValue: [4, 4] },
                vertical: { color: gridColor, style: 'dashed', dashValue: [4, 4] }
            },
            candle: {
                bar: {
                    upColor: upColor,
                    downColor: downColor,
                    noChangeColor: textColor
                },
                priceMark: {
                    high: { color: textColor, fontFamily },
                    low: { color: textColor, fontFamily }
                },
                tooltip: {
                    text: { color: textColor, fontFamily },
                    labels: { color: textColor, fontFamily },
                    values: { color: textColor, fontFamily },
                    defaultValue: { color: textColor, fontFamily }
                }
            },
            indicator: {
                tooltip: {
                    text: { color: textColor, fontFamily },
                    labels: { color: textColor, fontFamily },
                    values: { color: textColor, fontFamily }
                }
            },
            xAxis: {
                axisLine: { color: gridColor },
                tickLine: { color: gridColor },
                tickText: { color: textColor, fontFamily }
            },
            yAxis: {
                axisLine: { color: gridColor },
                tickLine: { color: gridColor },
                tickText: { color: textColor, fontFamily }
            },
            separator: { color: gridColor },
            crosshair: {
                horizontal: {
                    line: { color: gridColor, style: 'dashed' },
                    text: { backgroundColor: crosshairBg, color: crosshairText, fontFamily }
                },
                vertical: {
                    line: { color: gridColor, style: 'dashed' },
                    text: { backgroundColor: crosshairBg, color: crosshairText, fontFamily }
                }
            }
        };

        chartInstanceRef.current.setStyles(styles);
    }, []);

    // Apply theme when prop changes
    useEffect(() => {
        // Delay to allow React/DaisyUI to propagate theme updates to the DOM
        const timer = setTimeout(applyThemeStyles, 100);
        return () => clearTimeout(timer);
    }, [theme, applyThemeStyles]);

    // Get interval duration in milliseconds
    const getIntervalMs = useCallback((intv) => {
        const intervalMap = {
            '1m': 60 * 1000,
            '5m': 5 * 60 * 1000,
            '15m': 15 * 60 * 1000,
            '30m': 30 * 60 * 1000,
            '1h': 60 * 60 * 1000,
            '1d': 24 * 60 * 60 * 1000,
            '1w': 7 * 24 * 60 * 60 * 1000,
            '1M': 30 * 24 * 60 * 60 * 1000,
        };
        return intervalMap[intv] || 60 * 60 * 1000; // default to 1h
    }, []);

    // Extract trend lines from overlays
    const extractTrendLines = (chart, ids) => {
        const lines = [];
        const dataList = chart.getDataList() || [];
        const intervalMs = getIntervalMs(intervalRef.current);

        ids.forEach((id, index) => {
            const overlay = chart.getOverlayById(id);
            if (overlay && overlay.name === 'segment') {
                // segment is the trend line type in KLineCharts
                const points = overlay.points || [];
                if (points.length >= 2) {
                    let x1 = points[0].timestamp;
                    let x2 = points[1].timestamp;
                    const y1 = points[0].value;
                    const y2 = points[1].value;

                    // Handle case where timestamp is missing (drawing beyond data range)
                    // This happens when user draws trend line into the future
                    if (!x1 && points[0].dataIndex !== undefined && dataList.length > 0) {
                        // Calculate timestamp from dataIndex
                        const lastDataIndex = dataList.length - 1;
                        const lastTimestamp = dataList[lastDataIndex]?.timestamp || newestTimestampRef.current;
                        const indexDiff = points[0].dataIndex - lastDataIndex;
                        x1 = lastTimestamp + (indexDiff * intervalMs);
                    }

                    if (!x2 && points[1].dataIndex !== undefined && dataList.length > 0) {
                        // Calculate timestamp from dataIndex for future points
                        const lastDataIndex = dataList.length - 1;
                        const lastTimestamp = dataList[lastDataIndex]?.timestamp || newestTimestampRef.current;
                        const indexDiff = points[1].dataIndex - lastDataIndex;
                        x2 = lastTimestamp + (indexDiff * intervalMs);
                    }

                    // Fallback: if still no timestamp, use current time + offset
                    if (!x1) {
                        x1 = Date.now();
                    }
                    if (!x2) {
                        // If x2 is still missing, extrapolate from x1 based on visual distance
                        // This is a fallback - estimate future time based on chart width
                        x2 = x1 + (30 * intervalMs); // Default to 30 intervals ahead
                    }

                    console.log('[extractTrendLines] Extracted line:', {
                        id,
                        x1: new Date(x1).toISOString(),
                        x2: new Date(x2).toISOString(),
                        y1,
                        y2,
                        points: points.map(p => ({
                            timestamp: p.timestamp,
                            dataIndex: p.dataIndex,
                            value: p.value
                        })),
                    });

                    lines.push({
                        id: id,
                        name: `Trend Line ${index + 1}`,
                        x1: x1,
                        y1: y1,
                        x2: x2,
                        y2: y2,
                        symbol: symbol,
                        createdAt: new Date().toISOString(),
                    });
                }
            }
        });
        return lines;
    };

    // Update trend lines and notify parent
    const updateTrendLines = (chart, ids) => {
        const lines = extractTrendLines(chart, ids);
        setTrendLines(lines);
        if (onTrendLinesChange) {
            onTrendLinesChange(lines);
        }
    };

    const addDrawing = (type) => {
        if (chartInstanceRef.current) {
            const overlayId = chartInstanceRef.current.createOverlay({
                name: type,
                onDrawEnd: () => {
                    // When drawing is complete, update trend lines
                    setTimeout(() => {
                        if (chartInstanceRef.current) {
                            const newIds = [...overlayIds, overlayId];
                            updateTrendLines(chartInstanceRef.current, newIds);
                        }
                    }, 100);
                },
            });
            if (overlayId) {
                setOverlayIds(prev => {
                    const newIds = [...prev, overlayId];
                    return newIds;
                });
                console.log('Created overlay:', overlayId);
            }
        }
    };

    const toggleIndicator = (indicatorConfig) => {
        if (!chartInstanceRef.current) return;

        const { value, category } = indicatorConfig;

        if (activeIndicators.has(value)) {
            // Remove indicator
            if (category === 'overlay') {
                chartInstanceRef.current.removeIndicator('candle_pane', value);
            } else {
                // Sub-pane indicators need to be removed by their pane id
                chartInstanceRef.current.removeIndicator(value, value);
            }
            setActiveIndicators(prev => {
                const next = new Set(prev);
                next.delete(value);
                return next;
            });
            console.log('Removed indicator:', value);
        } else {
            // Add indicator
            const isOverlay = category === 'overlay';
            // For sub-pane indicators, set a fixed height so they don't shrink the main chart
            const paneOptions = isOverlay ? {} : { height: 120, minHeight: 100 };
            const indicatorId = chartInstanceRef.current.createIndicator(value, isOverlay, { id: value, paneOptions });

            // After adding a sub-pane indicator, ensure the main candle pane maintains its height
            if (!isOverlay) {
                chartInstanceRef.current.setPaneOptions({
                    id: 'candle_pane',
                    minHeight: 400,
                });
            }

            setActiveIndicators(prev => new Set([...prev, value]));
            console.log('Created indicator:', indicatorId);
        }
    };

    const clearAllIndicators = () => {
        if (!chartInstanceRef.current) return;

        activeIndicators.forEach(value => {
            const config = INDICATOR_OPTIONS.find(i => i.value === value);
            if (config) {
                if (config.category === 'overlay') {
                    chartInstanceRef.current.removeIndicator('candle_pane', value);
                } else {
                    chartInstanceRef.current.removeIndicator(value, value);
                }
            }
        });
        setActiveIndicators(new Set());
        console.log('Cleared all indicators');
    };

    const handleGetData = () => {
        if (chartInstanceRef.current) {
            // Get all overlays (drawings) using tracked IDs
            const overlayData = overlayIds.map(id => {
                const overlay = chartInstanceRef.current.getOverlayById(id);
                return overlay;
            }).filter(Boolean); // Remove any null values

            console.log('Drawings (Overlays):', overlayData);
            setDrawings(overlayData);

            // Get indicators from all panes
            const allIndicators = chartInstanceRef.current.getIndicatorByPaneId();
            console.log('Indicators:', allIndicators);

            // Convert Map to array for display
            let indicatorArray = [];
            if (allIndicators instanceof Map) {
                allIndicators.forEach((paneIndicators, paneId) => {
                    if (paneIndicators instanceof Map) {
                        paneIndicators.forEach((indicator, name) => {
                            indicatorArray.push({
                                paneId,
                                name,
                                ...indicator
                            });
                        });
                    }
                });
            }

            setIndicators(indicatorArray);

            alert(`Extracted ${overlayData.length} drawings and ${indicatorArray.length} indicators. Check console and preview below.`);
        }
    };

    return (
        <div className="flex flex-col relative overflow-y-auto">
            {/* Loading Overlay */}
            {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-base-100/80 z-10">
                    <div className="text-center">
                        <span className="loading loading-spinner loading-lg"></span>
                        <p className="text-sm mt-2">Loading {symbol} data...</p>
                    </div>
                </div>
            )}

            {/* Loading More Indicator */}
            {loadingMore && (
                <div className="absolute top-16 left-2 z-10 flex items-center gap-2 bg-base-100/90 px-3 py-1 rounded shadow">
                    <span className="loading loading-spinner loading-xs"></span>
                    <span className="text-xs">Loading more data...</span>
                </div>
            )}

            {/* Error Alert */}
            {error && (
                <div className="alert alert-error mb-4">
                    <span>{error}</span>
                </div>
            )}

            {/* Data Range Info */}
            {dataRange.from && dataRange.to && (
                <div className="mb-2 flex gap-2 items-center flex-wrap">
                    <span className="badge badge-outline badge-sm">
                        Data: {dataRange.from} - {dataRange.to}
                    </span>
                    {noMoreData && (
                        <span className="badge badge-warning badge-outline badge-sm">
                            No older data available
                        </span>
                    )}
                </div>
            )}

            {/* Interval Selector */}
            <div className="mb-4 flex gap-4 items-center flex-wrap">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Interval:</span>
                    <select
                        className="select select-bordered select-sm"
                        value={selectedInterval}
                        onChange={(e) => setSelectedInterval(e.target.value)}
                        disabled={loading}
                    >
                        {INTERVAL_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Live Stream Controls */}
            <div className="mb-4 flex gap-4 items-center flex-wrap">
                <label className="label cursor-pointer gap-2">
                    <span className="text-sm text-sm">Live Stream</span>
                    <input
                        type="checkbox"
                        className="toggle toggle-primary toggle-sm"
                        checked={liveStreamEnabled}
                        onChange={(e) => setLiveStreamEnabled(e.target.checked)}
                    />
                </label>

                {/* Connection Status */}
                {liveStreamEnabled && (
                    <span className={`badge badge-sm gap-1 ${isStreamConnected ? 'badge-success' : 'badge-warning'} badge-outline`}>
                        <span className={`w-2 h-2 rounded-full ${isStreamConnected ? 'bg-success' : 'bg-warning'}`}></span>
                        {isStreamConnected ? 'Connected' : 'Connecting...'}
                    </span>
                )}

                {/* Tick Counter */}
                {liveStreamEnabled && isStreamConnected && (
                    <span className="badge badge-outline badge-sm">Ticks: {tickCount}</span>
                )}

                {/* Last Price */}
                {lastTick && (
                    <span className="badge badge-info badge-sm">
                        Last: ₹{parseFloat(lastTick.closePrice).toFixed(2)}
                    </span>
                )}

                {/* Stream Error */}
                {streamError && (
                    <span className="badge badge-error badge-outline badge-sm">
                        Error: {streamError}
                    </span>
                )}
            </div>

            <div className="mb-4 p-4 rounded-lg bg-base-100">
                <h4 className="text-sm font-medium mb-2">Drawing Tools</h4>
                <div className="join mb-2">
                    <button className="btn btn-sm join-item" onClick={() => addDrawing('priceLine')}>Price Line</button>
                    <button className="btn btn-sm join-item" onClick={() => addDrawing('segment')}>Trend Line</button>
                    <button className="btn btn-sm join-item btn-error btn-outline" onClick={() => {
                        if (chartInstanceRef.current) {
                            chartInstanceRef.current.removeOverlay(); // Removes all overlays if no ID passed
                            setOverlayIds([]);
                            setDrawings([]);
                            setTrendLines([]);
                            if (onTrendLinesChange) onTrendLinesChange([]);
                        }
                    }}>Clear Drawings</button>
                </div>

                <h4 className="text-sm font-medium mb-2 mt-4">Technical Indicators</h4>

                {/* Overlay Indicators */}
                <div className="mb-3">
                    <span className="text-xs text-base-content/60 mb-1 block">Overlay (on chart)</span>
                    <div className="flex flex-wrap gap-1">
                        {INDICATOR_OPTIONS.filter(i => i.category === 'overlay').map(indicator => (
                            <button
                                key={indicator.value}
                                className={`btn btn-xs ${activeIndicators.has(indicator.value) ? 'btn-primary' : 'btn-ghost border-base-300'}`}
                                onClick={() => toggleIndicator(indicator)}
                                title={indicator.description}
                            >
                                {indicator.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Sub-pane Indicators */}
                <div className="mb-3">
                    <span className="text-xs text-base-content/60 mb-1 block">Sub-pane (below chart)</span>
                    <div className="flex flex-wrap gap-1">
                        {INDICATOR_OPTIONS.filter(i => i.category === 'sub-pane').map(indicator => (
                            <button
                                key={indicator.value}
                                className={`btn btn-xs ${activeIndicators.has(indicator.value) ? 'btn-primary' : 'btn-ghost border-base-300'}`}
                                onClick={() => toggleIndicator(indicator)}
                                title={indicator.description}
                            >
                                {indicator.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Clear All Indicators Button */}
                {activeIndicators.size > 0 && (
                    <button
                        className="btn btn-xs btn-error btn-outline"
                        onClick={clearAllIndicators}
                    >
                        Clear All Indicators ({activeIndicators.size})
                    </button>
                )}

                <div className="mt-2">
                    <button className="btn btn-success btn-sm" onClick={handleGetData}>
                        Get Data
                    </button>
                </div>
            </div>

            <div
                ref={chartContainerRef}
                className="w-full flex-shrink-0 bg-base-100"
                style={{
                    // Calculate dynamic height: 400px for main chart + 130px per active sub-pane indicator + buffer
                    height: `${400 + ([...activeIndicators].filter(ind => {
                        const config = INDICATOR_OPTIONS.find(i => i.value === ind);
                        return config && config.category === 'sub-pane';
                    }).length * 130) + 50}px`
                }}
            />

            {(drawings.length > 0 || indicators.length > 0) && (
                <div className="mt-4">
                    {drawings.length > 0 && (
                        <div className="card card-border bg-base-100 mb-4 p-4 max-h-48 overflow-auto">
                            <h4 className="text-sm font-medium mb-2">
                                Extracted Drawings ({drawings.length})
                            </h4>
                            <pre className="text-xs overflow-auto m-0">
                                {JSON.stringify(drawings, null, 2)}
                            </pre>
                        </div>
                    )}
                    {indicators.length > 0 && (
                        <div className="card card-border bg-base-100 p-4 max-h-48 overflow-auto">
                            <h4 className="text-sm font-medium mb-2">
                                Extracted Indicators ({indicators.length})
                            </h4>
                            <pre className="text-xs overflow-auto m-0">
                                {JSON.stringify(indicators, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StockChart;
